/**
 * Mockup de health de servicios por proyecto.
 *
 * Sin endpoint real todavía — la data viene de un mapeo por slug para
 * cumplir el caso de uso de propuesta/screenshots. Si más adelante
 * conectamos a métricas reales, este componente acepta el array
 * directo como prop para reemplazar el mock.
 */
import {
  Activity,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Server,
  Smartphone,
  Globe,
  Database,
  Cloud,
  GitCommit,
  Clock,
} from "lucide-react";

type ServiceStatus = "up" | "degraded" | "down";
type ServiceKind = "backend" | "mobile" | "web" | "api" | "worker" | "cdn" | "database";

interface ServiceHealth {
  id: string;
  name: string;
  kind: ServiceKind;
  environment: "prod" | "staging";
  status: ServiceStatus;
  uptimePct: number;
  latencyMsP95: number | null;
  version: string;
  commitShort: string;
  deployedAt: string; // ISO
  url?: string;
  stack?: string;
  host?: string;
}

const STATUS_BADGE: Record<ServiceStatus, string> = {
  up: "badge-success",
  degraded: "badge-warning",
  down: "badge-error",
};
const STATUS_LABEL: Record<ServiceStatus, string> = {
  up: "Operativo",
  degraded: "Degradado",
  down: "Caído",
};
const STATUS_TINT: Record<ServiceStatus, string> = {
  up: "bg-success/15 text-success",
  degraded: "bg-warning/15 text-warning",
  down: "bg-error/15 text-error",
};

const KIND_ICON: Record<ServiceKind, typeof Server> = {
  backend: Server,
  mobile: Smartphone,
  web: Globe,
  api: Activity,
  worker: Cloud,
  cdn: Cloud,
  database: Database,
};
const KIND_LABEL: Record<ServiceKind, string> = {
  backend: "Backend",
  mobile: "Mobile",
  web: "Web",
  api: "API",
  worker: "Worker",
  cdn: "CDN",
  database: "Base de datos",
};

function getMockServicesBySlug(slug: string): ServiceHealth[] {
  if (slug.toLowerCase().includes("cravioto")) {
    return CRAVIOTO_SERVICES;
  }
  return GENERIC_SERVICES;
}

// Mock fechas relativas al "ahora" del screenshot — todas en pasado
// reciente para que se vea consistente y profesional.
const CRAVIOTO_SERVICES: ServiceHealth[] = [
  {
    id: "1",
    name: "App de vendedores",
    kind: "mobile",
    environment: "prod",
    status: "up",
    uptimePct: 99.97,
    latencyMsP95: 184,
    version: "2.4.1",
    commitShort: "a7f3c2d",
    deployedAt: "2026-06-26T15:42:00Z",
    stack: "Ionic · Capacitor · Android",
    host: "Google Play Store",
  },
  {
    id: "2",
    name: "Sync FTP · Ventas y Productos",
    kind: "worker",
    environment: "prod",
    status: "up",
    uptimePct: 99.82,
    latencyMsP95: 312,
    version: "1.6.4",
    commitShort: "9e7b3f1",
    deployedAt: "2026-06-22T08:10:00Z",
    stack: "Node.js · node-ftp · cron",
    host: "EC2 t3.small · us-east-1",
  },
  {
    id: "3",
    name: "B2B Solutions · Storefront",
    kind: "web",
    environment: "prod",
    status: "up",
    uptimePct: 99.99,
    latencyMsP95: 112,
    version: "1.8.0",
    commitShort: "f291b04",
    deployedAt: "2026-06-28T09:15:00Z",
    url: "https://b2b.cravioto.mx",
    stack: "Next.js · Tailwind",
    host: "Vercel",
  },
  {
    id: "4",
    name: "Medusa Backend",
    kind: "api",
    environment: "prod",
    status: "up",
    uptimePct: 99.94,
    latencyMsP95: 87,
    version: "v1.20.6",
    commitShort: "d8e1a96",
    deployedAt: "2026-06-29T22:30:00Z",
    stack: "Medusa 1 · Node.js · Express",
    host: "EC2 t3.medium · us-east-1",
  },
  {
    id: "5",
    name: "PostgreSQL · Medusa DB",
    kind: "database",
    environment: "prod",
    status: "up",
    uptimePct: 99.98,
    latencyMsP95: 14,
    version: "PG 15.6",
    commitShort: "—",
    deployedAt: "2026-04-12T11:00:00Z",
    stack: "PostgreSQL 15 · pgBouncer",
    host: "EC2 t3.medium · us-east-1",
  },
  {
    id: "6",
    name: "CDN Imágenes Productos",
    kind: "cdn",
    environment: "prod",
    status: "up",
    uptimePct: 100.0,
    latencyMsP95: 38,
    version: "—",
    commitShort: "—",
    deployedAt: "2026-05-22T12:00:00Z",
    stack: "Cloudflare CDN",
    host: "Edge global",
  },
];

const GENERIC_SERVICES: ServiceHealth[] = [
  {
    id: "g1",
    name: "Sitio principal",
    kind: "web",
    environment: "prod",
    status: "up",
    uptimePct: 99.95,
    latencyMsP95: 124,
    version: "—",
    commitShort: "—",
    deployedAt: "2026-06-25T10:00:00Z",
  },
  {
    id: "g2",
    name: "API Backend",
    kind: "api",
    environment: "prod",
    status: "up",
    uptimePct: 99.92,
    latencyMsP95: 95,
    version: "—",
    commitShort: "—",
    deployedAt: "2026-06-27T14:00:00Z",
  },
];

function timeAgoIso(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "hace un momento";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "hace un momento";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `hace ${days} d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return `hace ${weeks} sem`;
  const months = Math.floor(days / 30);
  return `hace ${months} mes${months === 1 ? "" : "es"}`;
}

function formatDeployFull(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function ServicesHealthPanel({
  projectSlug,
  services,
}: {
  projectSlug: string;
  services?: ServiceHealth[];
}) {
  const list = services ?? getMockServicesBySlug(projectSlug);
  if (list.length === 0) return null;

  const upCount = list.filter((s) => s.status === "up").length;
  const degradedCount = list.filter((s) => s.status === "degraded").length;
  const downCount = list.filter((s) => s.status === "down").length;

  const overallStatus: ServiceStatus =
    downCount > 0 ? "down" : degradedCount > 0 ? "degraded" : "up";

  return (
    <section className="card-elevated overflow-hidden">
      {/* Header con resumen */}
      <header className="px-5 py-4 border-b border-base-300/40 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`size-10 rounded-lg grid place-items-center ${STATUS_TINT[overallStatus]}`}>
            <Activity className="size-5" />
          </div>
          <div>
            <h2 className="font-display font-semibold text-base leading-tight">
              Health de servicios
            </h2>
            <p className="text-xs text-base-content/55 mt-0.5">
              Estado en tiempo real de los componentes que mantenemos en producción para este
              proyecto.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="badge badge-sm badge-success gap-1">
            <span className="inline-block size-1.5 rounded-full bg-success-content" /> {upCount} ok
          </span>
          {degradedCount > 0 && (
            <span className="badge badge-sm badge-warning gap-1">
              <span className="inline-block size-1.5 rounded-full bg-warning-content" />{" "}
              {degradedCount} degradado
            </span>
          )}
          {downCount > 0 && (
            <span className="badge badge-sm badge-error gap-1">
              <span className="inline-block size-1.5 rounded-full bg-error-content" /> {downCount}{" "}
              caído
            </span>
          )}
        </div>
      </header>

      <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
        {list.map((s) => (
          <ServiceCard key={s.id} svc={s} />
        ))}
      </ul>
    </section>
  );
}

function ServiceCard({ svc }: { svc: ServiceHealth }) {
  const Icon = KIND_ICON[svc.kind];
  return (
    <li className="rounded-lg border border-base-300/60 bg-base-100 p-4 flex flex-col gap-3 hover:border-base-300 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5 min-w-0 flex-1">
          <div className="size-9 rounded-md bg-base-200 grid place-items-center shrink-0">
            <Icon className="size-4.5 text-base-content/65" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-medium text-sm leading-tight truncate">{svc.name}</h3>
              <span className="badge badge-xs badge-ghost uppercase tracking-wider">
                {svc.environment}
              </span>
            </div>
            <div className="text-[11px] text-base-content/55 mt-0.5 leading-snug">
              {svc.stack ?? KIND_LABEL[svc.kind]}
            </div>
            {svc.host && (
              <div className="text-[10px] text-base-content/45 mt-0.5 leading-snug">
                {svc.host}
              </div>
            )}
          </div>
        </div>
        <span className={`badge badge-sm ${STATUS_BADGE[svc.status]} gap-1 shrink-0`}>
          {svc.status === "up" ? (
            <CheckCircle2 className="size-3" />
          ) : svc.status === "degraded" ? (
            <AlertTriangle className="size-3" />
          ) : (
            <AlertCircle className="size-3" />
          )}
          {STATUS_LABEL[svc.status]}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-md bg-base-200/50 px-2.5 py-1.5">
          <div className="text-[10px] uppercase tracking-wider text-base-content/45 font-semibold">
            Uptime · 30d
          </div>
          <div className="font-mono tabular-nums font-semibold mt-0.5">
            {svc.uptimePct.toFixed(2)}%
          </div>
        </div>
        <div className="rounded-md bg-base-200/50 px-2.5 py-1.5">
          <div className="text-[10px] uppercase tracking-wider text-base-content/45 font-semibold">
            Latencia p95
          </div>
          <div className="font-mono tabular-nums font-semibold mt-0.5">
            {svc.latencyMsP95 !== null ? `${svc.latencyMsP95} ms` : "—"}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-base-300/40 text-[11px] gap-2">
        <div
          className="flex items-center gap-1.5 text-base-content/70 min-w-0"
          title={formatDeployFull(svc.deployedAt)}
        >
          <Clock className="size-3 shrink-0" />
          <span className="truncate">Deploy {timeAgoIso(svc.deployedAt)}</span>
        </div>
        {svc.version !== "—" && (
          <div className="flex items-center gap-1 font-mono text-base-content/65 shrink-0">
            <GitCommit className="size-3" />
            <span>{svc.version}</span>
            {svc.commitShort !== "—" && (
              <span className="text-base-content/40">·{svc.commitShort}</span>
            )}
          </div>
        )}
      </div>

      {/* Mini "trend" — barras decorativas, sutilmente animan vivacidad */}
      <div className="flex items-end gap-0.5 h-5 -mt-1">
        {Array.from({ length: 32 }).map((_, i) => {
          // pseudo-random determinístico por id+i — sin Math.random para
          // que el screenshot quede igual entre renders
          const hash = (svc.id.charCodeAt(0) * 31 + i * 7) % 100;
          const baseHeight =
            svc.status === "down"
              ? 30
              : svc.status === "degraded"
                ? 55
                : 75;
          const h = Math.min(95, baseHeight + (hash % 25));
          const isAnomaly =
            (svc.status === "degraded" && hash % 7 === 0) ||
            (svc.status === "down" && hash % 4 === 0);
          return (
            <div
              key={i}
              className={
                "w-1 rounded-sm " +
                (isAnomaly
                  ? svc.status === "down"
                    ? "bg-error/70"
                    : "bg-warning/70"
                  : svc.status === "down"
                    ? "bg-error/40"
                    : "bg-success/55")
              }
              style={{ height: `${h}%` }}
            />
          );
        })}
      </div>
    </li>
  );
}
